import { useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';

interface PerformanceMetrics {
  FCP: number | null; // First Contentful Paint
  LCP: number | null; // Largest Contentful Paint
  FID: number | null; // First Input Delay
  CLS: number | null; // Cumulative Layout Shift
  TTFB: number | null; // Time to First Byte
}

export function usePerformanceMonitor() {
  const logMetric = useCallback((name: string, value: number) => {
    if (process.env.NODE_ENV === 'development') {
      logger.info(`🚀 Performance Metric - ${name}: ${value.toFixed(2)}ms`);
    }
    
    // Send to analytics service in production
    if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
      // You can integrate with Google Analytics, DataDog, etc.
      // gtag('event', 'performance_metric', {
      //   metric_name: name,
      //   metric_value: value,
      // });
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      return;
    }

    const metrics: PerformanceMetrics = {
      FCP: null,
      LCP: null,
      FID: null,
      CLS: null,
      TTFB: null,
    };

    // First Contentful Paint
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcpEntry = entries.find(entry => entry.name === 'first-contentful-paint');
      if (fcpEntry) {
        metrics.FCP = fcpEntry.startTime;
        logMetric('First Contentful Paint', fcpEntry.startTime);
      }
    });

    // Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        metrics.LCP = lastEntry.startTime;
        logMetric('Largest Contentful Paint', lastEntry.startTime);
      }
    });

    // First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (entry.processingStart && entry.startTime) {
          const fid = entry.processingStart - entry.startTime;
          metrics.FID = fid;
          logMetric('First Input Delay', fid);
        }
      });
    });

    // Cumulative Layout Shift
    let clsScore = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsScore += entry.value;
        }
      });
      metrics.CLS = clsScore;
      logMetric('Cumulative Layout Shift', clsScore * 1000); // Convert to ms for consistency
    });

    // Time to First Byte
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry) {
      const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
      metrics.TTFB = ttfb;
      logMetric('Time to First Byte', ttfb);
    }

    // Start observing
    try {
      fcpObserver.observe({ entryTypes: ['paint'] });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      fidObserver.observe({ entryTypes: ['first-input'] });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      logger.warn('Performance Observer not fully supported:', error);
    }

    // Cleanup
    return () => {
      fcpObserver.disconnect();
      lcpObserver.disconnect();
      fidObserver.disconnect();
      clsObserver.disconnect();
    };
  }, [logMetric]);

  // Resource loading monitoring
  useEffect(() => {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        // Log slow resources (>1s)
        if (entry.duration > 1000) {
          logger.warn(`⚠️ Slow Resource: ${entry.name} took ${entry.duration.toFixed(2)}ms`);
        }
      });
    });

    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch (error) {
      logger.warn('Resource Performance Observer not supported:', error);
    }

    return () => observer.disconnect();
  }, []);

  return { logMetric };
}